'use client';

import { useShallow } from 'zustand/react/shallow';

import { useSpaceStore } from './provider';
import type { UseCurrentSpaceResult } from './types';

// ------------- Primary Hook -------------

/**
 * Custom hook to access the current space ID and related utilities.
 *
 * This is the recommended hook for most use cases. It provides:
 * - Current space ID
 * - Function to change space
 * - Loading/hydration state
 * - Available spaces list
 *
 * Usage:
 * ```tsx
 * const { spaceId, setSpace, isLoaded, availableSpaces } = useCurrentSpace();
 *
 * // Use spaceId in API calls
 * const data = await fetchData(spaceId);
 *
 * // Switch to a different space
 * setSpace('new-space-id');
 *
 * // Show space selector
 * availableSpaces.map(space => <option key={space.id}>{space.id}</option>)
 * ```
 *
 * @returns Object containing space state and actions
 */
export function useCurrentSpace(): UseCurrentSpaceResult {
  // Use single selector with shallow comparison for optimal performance
  const { currentSpaceId, setCurrentSpace, isHydrated, availableSpaces } = useSpaceStore(
    useShallow(state => ({
      currentSpaceId: state.currentSpaceId,
      setCurrentSpace: state.setCurrentSpace,
      isHydrated: state.isHydrated,
      availableSpaces: state.availableSpaces,
    })),
  );

  return {
    spaceId: currentSpaceId,
    setSpace: setCurrentSpace,
    isLoaded: isHydrated,
    hasSpace: currentSpaceId !== '',
    availableSpaces,
  };
}

// ------------- Individual Selectors -------------

/**
 * Get current space ID only.
 * Use when you only need the space ID and don't need other state.
 */
export function useSpaceId(): string {
  return useSpaceStore(state => state.currentSpaceId);
}

/**
 * Check if space store is hydrated.
 * Use to gate operations that require hydration.
 */
export function useIsSpaceHydrated(): boolean {
  return useSpaceStore(state => state.isHydrated);
}

/**
 * Get the setCurrentSpace action.
 * Use when you only need to set the space.
 */
export function useSetCurrentSpace(): (spaceId: string) => void {
  return useSpaceStore(state => state.setCurrentSpace);
}

/**
 * Get available spaces list.
 * Use when you need to display space options.
 */
export function useAvailableSpaces() {
  return useSpaceStore(state => state.availableSpaces);
}

/**
 * Get the setAvailableSpaces action.
 * Use to update spaces list when loaded from API.
 */
export function useSetAvailableSpaces() {
  return useSpaceStore(state => state.setAvailableSpaces);
}

// ------------- Backwards Compatibility -------------

/**
 * @deprecated Use useCurrentSpace instead
 * Backwards-compatible hook that returns the old API shape
 */
export function useSpaceContext() {
  // Use single selector with shallow comparison for optimal performance
  return useSpaceStore(
    useShallow(state => ({
      currentSpaceId: state.currentSpaceId,
      setCurrentSpace: state.setCurrentSpace,
      isSpaceLoaded: state.isHydrated,
    })),
  );
}
